import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Card,
  CardTitle,
  FilterSelect,
  PageBody,
  Spinner,
  type FilterOption,
} from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { DateRangeFilter } from '@/components/DateRangeFilter.tsx';
import type { DateRangeOption } from '@/components/dateRangeOptions.ts';
import { CostTableCard } from '@/components/dashboard/costs/CostTableCard.tsx';
import { CostTotals } from '@/components/dashboard/costs/CostTotals.tsx';
import { LatencyCard } from '@/components/dashboard/costs/LatencyCard.tsx';
import { TokenMixCard } from '@/components/dashboard/costs/TokenMixCard.tsx';
import {
  groupColumns,
  organisationColumns,
  productColumns,
  userColumns,
  vendorColumns,
} from '@/components/dashboard/costs/costColumns.tsx';
import { VENDOR_NAMES, vendorName } from '@/components/dashboard/costs/costFormat.ts';
import { useOrganisationFilter } from '@/lib/activeOrganisation.ts';
import { requireInternalOrganisation } from '@/lib/staffGate.ts';
import {
  COSTS_SINCE_NOTE,
  fetchUsageCostDashboard,
  type UsageCostByGroup,
  type UsageCostByOrganisation,
  type UsageCostByProduct,
  type UsageCostByUser,
  type UsageCostByVendor,
  type UsageCostRange,
} from '@/lib/usageCost.ts';
import { formatCurrency } from '@/utils/currencyUtils.ts';

export const Route = createFileRoute('/_app/dashboard/costs')({
  staticData: { orgScope: 'optional' },
  beforeLoad: ({ context }) => requireInternalOrganisation(context.queryClient),
  component: CostsDashboardPage,
});

/*
 * The five windows the ledger answers for. `days` is part of the shared option
 * shape and unused here: which dates a named range covers is the backend's, not
 * this page's — it is the one that knows when "this month" started.
 */
const RANGE_OPTIONS: readonly DateRangeOption[] = [
  { value: 'today', label: 'Today', days: 1 },
  { value: 'last_7d', label: 'Last 7 days', days: 7 },
  { value: 'last_30d', label: 'Last 30 days', days: 30 },
  { value: 'this_month', label: 'This month', days: null },
  { value: 'all_time', label: 'All time', days: null },
];

/**
 * What every billable use at a vendor has cost, and where the money went. One
 * request answers the whole page: it is all the same rows counted differently,
 * and separate endpoints would disagree the moment a row lands between two.
 */
export function CostsDashboardPage() {
  const [range, setRange] = useState<UsageCostRange>('last_30d');
  const [vendor, setVendor] = useState('');
  const organisationId = useOrganisationFilter()?.id ?? null;

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['usageCostDashboard', range, organisationId, vendor],
    queryFn: () =>
      fetchUsageCostDashboard(range, { organisationId, vendor: vendor || null }),
    placeholderData: keepPreviousData,
  });

  /* A vendor that only exists in the data (an old provider name) stays pickable. */
  const vendorIds = new Set([
    ...Object.keys(VENDOR_NAMES),
    ...(data?.by_vendor ?? []).map((row) => row.vendor),
  ]);
  const vendorOptions: FilterOption<string>[] = [
    { value: '', label: 'All vendors' },
    ...[...vendorIds].map((id) => ({ value: id, label: vendorName(id) })),
  ];

  const totals = data?.totals;

  const groupBreakdowns: { title: string; rows: UsageCostByGroup[] }[] = data
    ? [
        { title: 'By channel', rows: data.by_channel },
        { title: 'By purpose', rows: data.by_purpose },
        { title: 'By mode (sync / batch)', rows: data.by_mode },
      ]
    : [];

  return (
    <>
      <PageHeader
        title='Costs'
        subtitle={`What every model call, notetaker bot, transcription and voice cost us. ${COSTS_SINCE_NOTE}`}
        filters={
          <>
            <FilterSelect
              hideLabel
              label='Vendor'
              options={vendorOptions}
              value={vendor}
              onValueChange={setVendor}
            />
            <DateRangeFilter
              options={RANGE_OPTIONS}
              value={range}
              onChange={(value) => setRange(value as UsageCostRange)}
            />
          </>
        }
      />

      <PageBody className='flex flex-col gap-5'>
        {isPending && <Spinner className='size-5 text-ink-muted' />}

        {data && totals && (
          <>
            <CostTotals totals={totals} />

            <Card as='section' className='flex flex-col gap-3.5'>
              <CardTitle>Daily cost</CardTitle>
              <BarChart
                ariaLabel='Daily cost in US dollars'
                labels={data.daily.map((day) => day.day.slice(5))}
                series={[
                  { label: 'Cost (USD)', data: data.daily.map((day) => day.cost_usd) },
                ]}
                yFormat={formatCurrency}
                height={256}
                emptyTitle='No data in this window'
              />
            </Card>

            <CostTableCard<UsageCostByVendor>
              title='By vendor'
              label='Cost by vendor'
              columns={vendorColumns(totals.cost_usd)}
              rows={data.by_vendor}
              rowKey={(row) => `${row.vendor}/${row.unit}`}
            />

            <CostTableCard<UsageCostByProduct>
              title='By product'
              label='Cost by product'
              columns={productColumns}
              rows={data.by_product}
              rowKey={(row) => `${row.vendor}/${row.product}/${row.unit}`}
            />

            <CostTableCard<UsageCostByUser>
              title='Top users'
              label='Cost by user'
              columns={userColumns}
              rows={data.by_user}
              rowKey={(row) => row.user_id}
            />

            {/* Already narrowed to one organisation: a ranking of one says nothing. */}
            {organisationId === null && (
              <CostTableCard<UsageCostByOrganisation>
                title='Top organisations'
                label='Cost by organisation'
                columns={organisationColumns(totals.cost_usd)}
                rows={data.by_organisation}
                rowKey={(row) => row.organisation_id ?? 'none'}
              />
            )}

            <div className='grid grid-cols-1 gap-4 agenda:grid-cols-2'>
              {groupBreakdowns.map((group) => (
                <CostTableCard<UsageCostByGroup>
                  key={group.title}
                  title={group.title}
                  label={group.title}
                  columns={groupColumns}
                  rows={group.rows}
                  rowKey={(row) => row.key ?? 'none'}
                />
              ))}

              <TokenMixCard totals={totals} />
            </div>

            <LatencyCard latency={data.latency_by_model ?? []} />
          </>
        )}

        {/* A reload of an already-drawn page: the numbers below are the old ones. */}
        {isFetching && !isPending && <Spinner className='size-4 text-ink-muted' />}
      </PageBody>
    </>
  );
}
